import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useCallback } from 'react'
import { SignInButton, SignUpButton, useAuth } from '@clerk/clerk-react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { usePreviewSongStorage } from '@/hooks/usePreviewSongStorage'
import { SongCard } from '@/components/SongCard'

export const Route = createFileRoute('/preview/songs')({
  component: PreviewSongsPage,
})

// Fallback test song data if preview user doesn't have songs yet
const FALLBACK_TEST_SONG = {
  title: 'Test Song',
  prompt: 'Test generation for webhook testing',
  lyrics: '[Verse 1]\nThis is a test song\nFor preview purposes\n\n[Chorus]\nTest, test, test\nThis is just a test',
  tags: 'test, preview, demo',
  audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3', // Public test audio URL
  status: 'completed' as const,
  _creationTime: Date.now(),
}

function PreviewSongsPage() {
  const { isSignedIn } = useAuth()
  const [title, setTitle] = useState('')
  const [tags, setTags] = useState('')
  const [lyrics, setLyrics] = useState('')
  const [theme, setTheme] = useState('')
  const [showSignInModal, setShowSignInModal] = useState(false)

  const { loadFormState, saveFormState } = usePreviewSongStorage()
  
  // Fetch preview songs from Convex
  const previewSongs = useConvexQuery(api.preview.getPreviewSongs, {})

  // Load saved form state on mount
  useEffect(() => {
    const saved = loadFormState()
    if (saved) {
      if (saved.title && saved.title.length > 0) setTitle(saved.title)
      if (saved.tags && saved.tags.length > 0) setTags(saved.tags)
      if (saved.lyrics && saved.lyrics.length > 0) setLyrics(saved.lyrics)
      if (saved.theme && saved.theme.length > 0) setTheme(saved.theme)
    }
  }, [loadFormState])

  // Save to localStorage on blur
  const handleBlur = useCallback(() => {
    saveFormState({
      title,
      tags,
      lyrics,
      theme,
    })
  }, [title, tags, lyrics, theme, saveFormState])

  const handleCreateSong = (e: React.FormEvent) => {
    e.preventDefault()

    // Save current form values to localStorage
    saveFormState({
      title,
      tags,
      lyrics,
      theme,
    })

    // If user is not signed in, show sign-in modal
    if (!isSignedIn) {
      setShowSignInModal(true)
      return
    }

    // If signed in, redirect to app/songs (this shouldn't happen in preview mode)
    // But handle it just in case
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Preview Banner */}
        <div className="mb-4 bg-terminal-green/20 border-2 border-terminal-green p-3 text-center">
          <p className="text-terminal-green font-mono text-sm font-bold">
            [PREVIEW MODE] • Sign up to create and save your songs
          </p>
        </div>

        {/* Two Column Layout: 50% Test Song | 50% Creation Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: Preview Songs Display */}
          <div>
            <div className="mb-6 terminal-border p-6">
              <h2 className="text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
                [PREVIEW SONGS]
              </h2>
              <p className="text-trail-tan font-mono mb-4">
                Preview audio player functionality
              </p>
            </div>
            
            {/* Show preview songs from Convex, or fallback test song */}
            {previewSongs === undefined ? (
              <div className="terminal-border p-4 bg-black">
                <p className="text-trail-tan font-mono text-sm">Loading preview songs...</p>
              </div>
            ) : previewSongs.length > 0 ? (
              <div className="space-y-4">
                {previewSongs.map((song) => (
                  <SongCard
                    key={song._id}
                    generation={{
                      ...song,
                      status: song.status as 'pending' | 'processing' | 'completed' | 'failed',
                    }}
                  />
                ))}
              </div>
            ) : (
              <SongCard generation={FALLBACK_TEST_SONG} />
            )}
          </div>

          {/* RIGHT: Song Creation Form */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            <SongCreationForm
              title={title}
              setTitle={setTitle}
              tags={tags}
              setTags={setTags}
              lyrics={lyrics}
              setLyrics={setLyrics}
              theme={theme}
              setTheme={setTheme}
              onBlur={handleBlur}
              onSubmit={handleCreateSong}
              showSignInModal={showSignInModal}
              setShowSignInModal={setShowSignInModal}
              isSignedIn={isSignedIn || false}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// TestSongCard removed - using shared SongCard component

function SongCreationForm({
  title,
  setTitle,
  tags,
  setTags,
  lyrics,
  setLyrics,
  theme,
  setTheme,
  onBlur,
  onSubmit,
  showSignInModal,
  setShowSignInModal,
  isSignedIn: _isSignedIn,
}: {
  title: string
  setTitle: (value: string) => void
  tags: string
  setTags: (value: string) => void
  lyrics: string
  setLyrics: (value: string) => void
  theme: string
  setTheme: (value: string) => void
  onBlur: () => void
  onSubmit: (e: React.FormEvent) => void
  showSignInModal: boolean
  setShowSignInModal: (show: boolean) => void
  isSignedIn: boolean
}) {
  return (
    <div className="terminal-border p-6 bg-black">
      <h2 className="text-3xl font-bold text-terminal-green mb-4 font-mono tracking-wider">
        [CREATE SONG]
      </h2>
      <p className="text-trail-tan font-mono text-sm mb-6">
        Fill in the form below and sign in to create your song
      </p>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Music Theme/Style Prompt */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► MUSIC THEME / STYLE
          </label>
          <textarea
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            onBlur={onBlur}
            placeholder="Describe the music style you want...
Example: 'upbeat pop with electronic synths'
Example: 'melancholic acoustic guitar ballad'
Example: 'energetic rock with electric guitars'"
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
            rows={5}
          />
        </div>

        {/* Lyrics Input */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► SONG LYRICS
          </label>
          <textarea
            value={lyrics}
            onChange={(e) => setLyrics(e.target.value)}
            onBlur={onBlur}
            placeholder="Enter your song lyrics here...
[Verse 1]
Walking down the street...

[Chorus]
And I feel alive...

[Verse 2]
The sun is shining bright..."
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
            rows={10}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            data-1p-ignore
            data-lpignore="true"
          />
          <p className="text-trail-tan text-xs font-mono mt-2">
            💡 TIP: Use [Verse 1], [Chorus], [Bridge] tags for structure
          </p>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► TITLE <span className="text-trail-tan font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={onBlur}
            placeholder="Enter song title..."
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► TAGS <span className="text-trail-tan font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            onBlur={onBlur}
            placeholder="e.g., rock, upbeat, energetic (comma-separated)"
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
          />
          <p className="text-trail-tan text-xs font-mono mt-2">
            💡 Comma-separated keywords for genre, mood, instruments, etc.
          </p>
        </div>

        {/* Sign In Modal Trigger */}
        {showSignInModal && (
          <div className="bg-black border-2 border-terminal-green p-4 mb-4">
            <p className="text-terminal-green font-mono font-bold text-sm mb-4">
              Sign in to create your song
            </p>
            <div className="flex gap-3">
              <SignUpButton mode="modal">
                <button
                  type="button"
                  className="px-6 py-3 bg-terminal-green text-black font-bold font-mono text-sm border-2 border-terminal-green hover:bg-black hover:text-terminal-green transition-all"
                >
                  [SIGN UP]
                </button>
              </SignUpButton>
              <SignInButton mode="modal">
                <button
                  type="button"
                  className="px-6 py-3 bg-black text-trail-tan font-bold font-mono text-sm border-2 border-trail-tan hover:bg-trail-tan hover:text-black transition-all"
                >
                  [SIGN IN]
                </button>
              </SignInButton>
              <button
                type="button"
                onClick={() => setShowSignInModal(false)}
                className="px-6 py-3 bg-black text-gray-400 font-bold font-mono text-sm border-2 border-gray-600 hover:bg-gray-900 transition-all"
              >
                [CANCEL]
              </button>
            </div>
          </div>
        )}

        {/* CREATE SONG Button */}
        <button
          type="submit"
          disabled={!theme.trim() || !lyrics.trim()}
          className="w-full px-8 py-4 bg-terminal-green text-white font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow tracking-wider"
        >
          ► CREATE SONG ◄
        </button>
      </form>

      {/* Info Box */}
      <div className="mt-6 bg-black border-2 border-trail-tan p-4">
        <p className="text-trail-tan font-mono text-xs mb-2">
          <span className="text-sky-blue">►</span> What you&apos;ll get:
        </p>
        <ul className="space-y-1 text-trail-tan font-mono text-xs">
          <li><span className="text-terminal-green">•</span> AI-generated song with vocals</li>
          <li><span className="text-terminal-green">•</span> Your custom lyrics sung by AI</li>
          <li><span className="text-terminal-green">•</span> Music matching your theme/style</li>
          <li><span className="text-terminal-green">•</span> Ready for vocabulary extraction</li>
        </ul>
      </div>
    </div>
  )
}

